import React, { useState, useEffect } from 'react';
import { Add_skill, List_skill, List_industry, Delete_skill, Update_skill } from '../../Utils/Api';
import { toast } from 'react-toastify';

const Skills = () => {
  const [skills, setSkills] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    industry: '',
    tag: '',
    status: 0
  });
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchSkills();
    fetchIndustries();
  }, []);

  // Fetch all skills
  const fetchSkills = async () => {
    setLoading(true);
    try {
      const response = await List_skill();
      if (response && response.data?.data) {
        setSkills(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching skills:', error);
      toast.error('Failed to fetch skills');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all industries for dropdown
  const fetchIndustries = async () => {
    try {
      const response = await List_industry();
      if (response && response.data?.data) {
        setIndustries(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
      toast.error('Failed to fetch industries');
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission (Add/Update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter skill title');
      return;
    }
    if (!formData.industry) {
      toast.error('Please select an industry');
      return;
    }
    setLoading(true);
    try {
      if (editingId) {
        // Update existing skill
        await Update_skill(editingId, formData);
        toast.success('Skill updated successfully');
      } else {
        // Add new skill
        await Add_skill(formData);
        toast.success('Skill added successfully');
      }

      // Reset form and refresh list
      setFormData({ title: '', industry: '', tag: '', status: 0 });
      setEditingId(null);
      setShowModal(false);
      fetchSkills();
    } catch (error) {
      console.error('Error saving skill:', error);
      toast.error('Failed to save skill');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (skill) => {
    setFormData({
      title: skill.title,
      industry: skill.industry?._id || skill.industry || '',
      tag: skill.tag || '',
      status: skill.status || 0
    });
    setEditingId(skill._id || skill.id);
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteId) return;

    setLoading(true);
    try {
        await Delete_skill(deleteId);
      toast.success('Skill deleted successfully');
      setDeleteId(null);
      fetchSkills();
    } catch (error) {
      console.error('Error deleting skill:', error);
      toast.error('Failed to delete skill');
    } finally {
      setLoading(false);
    }
  };

  // Open add modal
  const handleAdd = () => {
    setFormData({ title: '', industry: '', tag: '', status: 0 });
    setEditingId(null);
    setShowModal(true);
  };

  // Get industry name by ID
  const getIndustryName = (industryData) => {
    if (typeof industryData === 'object' && industryData?.title) {
      return industryData.title;
    }
    const industry = industries.find(ind => ind._id === industryData);
    return industry?.title || 'N/A';
  };

  return (
    <>
      <div className="main-content ">
        <div className="row my-4 mx-1">
          <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
            <h1 className="page-title fw-semibold fs-18 mb-0 mt-2">Skills</h1>
            <div className="ms-md-1 ms-0 ">
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><a href="#">Management</a></li>
                  <li className="breadcrumb-item active" aria-current="page">Skills</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* Main Content Card */}
          <div className="card custom-card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="card-title mb-0">Skills List</h6>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleAdd}
              >
                <i className="ri-add-line me-1"></i> Add Skill
              </button>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table text-nowrap">
                  <thead className="table-primary">
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Skill Title</th>
                       <th scope="col">Industry</th>
                      <th scope="col">Tag</th>
                      <th scope="col">Status</th>
                      <th scope="col">Created Date</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    )}

                    {!loading && skills.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          No skills found
                        </td>
                      </tr>
                    )}

                    {!loading && skills.map((skill, index) => (
                      <tr key={skill._id || skill.id}>
                        <th scope="row">{index + 1}</th>
                        <td>{skill.title}</td>
                        <td>{getIndustryName(skill.industry)}</td>
                         <td>
                          <span className="badge bg-primary-transparent">{skill.tag}</span>
                        </td>
                        <td>
                          {skill.status === 0 ? (
                            <span className="badge bg-success-transparent">Active</span>
                          ) : (
                            <span className="badge bg-danger-transparent">Inactive</span>
                          )}
                        </td>
                        <td>{new Date(skill.createdAt || Date.now()).toLocaleDateString()}</td>
                        <td>
                          <div className="hstack gap-2 fs-15">
                            <button
                              onClick={() => handleEdit(skill)}
                              className="btn btn-icon btn-sm btn-info-transparent rounded-pill"
                              title="Edit"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              onClick={() => setDeleteId(skill._id || skill.id)}
                              className="btn btn-icon btn-sm btn-danger-transparent rounded-pill"
                              data-bs-toggle="modal"
                              data-bs-target="#deleteModal"
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Add/Edit Modal */}
          {showModal && (
            <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingId ? 'Edit Skill' : 'Add New Skill'}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>
                  <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                      {/* Skill Title */}
                      <div className="mb-3">
                        <label htmlFor="title" className="form-label">Skill Title</label>
                        <input
                          type="text"
                          className="form-control"
                          id="title"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          placeholder="e.g., React JS"
                          required
                        />
                      </div>

                      {/* Industry Dropdown */}
                      <div className="mb-3">
                        <label htmlFor="industry" className="form-label">Industry</label>
                        <select
                          className="form-select"
                          id="industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select an industry</option>
                          {industries.map(industry => (
                            <option key={industry._id} value={industry._id}>
                              {industry.title}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Tag */}
                       <div className="mb-3">
                        <label htmlFor="tag" className="form-label">Tag</label>
                        <input
                          type="text"
                          className="form-control"
                          id="tag"
                          name="tag"
                          value={formData.tag}
                          onChange={handleInputChange}
                          placeholder="e.g., Frontend"
                        />
                      </div>

                      {/* Status */}
                      <div className="mb-3">
                        <label htmlFor="status" className="form-label">Status</label>
                        <select
                          className="form-select"
                          id="status"
                          name="status"
                          value={formData.status}
                          onChange={(e) => setFormData(prev => ({ ...prev, status: parseInt(e.target.value) }))}
                          required
                        >
                          <option value={0}>Active</option>
                          <option value={1}>Inactive</option>
                        </select>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            {editingId ? 'Updating...' : 'Adding...'}
                          </>
                        ) : (
                          editingId ? 'Update' : 'Add'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          <div className="modal fade" id="deleteModal" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    onClick={() => setDeleteId(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete this skill?</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                    onClick={() => setDeleteId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    data-bs-dismiss="modal"
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    {loading ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Skills;
